'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Navigation } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useDestinationStore } from '@/stores/destinationStore';
import { getListingCommutes } from '@/lib/api';
import { Skeleton } from '@/components/ui/Skeleton';

interface Commute {
  destination_id: string;
  label: string;
  duration_minutes?: number;
  distance_km?: number;
  mode?: string;
  driving_minutes?: number;
  transit_minutes?: number;
}

interface Props {
  listingId: string;
}

export function CommuteDisplay({ listingId }: Props) {
  const { user, isInitialized } = useAuthStore();
  const { destinations, fetchDestinations } = useDestinationStore();
  const pathname = usePathname();
  // null = not fetched yet; [] = fetched, empty
  const [commutes, setCommutes] = useState<Commute[] | null>(null);

  useEffect(() => {
    if (user) {
      fetchDestinations();
    }
  }, [user, fetchDestinations]);

  const shouldFetch = Boolean(user && destinations.length > 0);

  useEffect(() => {
    if (!shouldFetch) return;
    let cancelled = false;

    (async () => {
      try {
        const result = (await getListingCommutes(listingId)) as unknown;
        if (cancelled) return;
        const list = Array.isArray(result)
          ? result
          : ((result as { commutes?: Commute[] })?.commutes ?? []);
        setCommutes(list);
      } catch {
        if (!cancelled) setCommutes([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [listingId, shouldFetch, destinations]);

  // Signed out (or still resolving auth) — nothing personalized to show yet.
  if (!isInitialized) {
    return <Skeleton className="h-24 w-full" />;
  }
  // Guests get an honest invitation instead of a dead-end blank: this is a real
  // feature they can unlock, not fabricated data.
  if (!user) {
    return (
      <div className="glass rounded-card p-5 text-center">
        <p className="mb-2 text-ink-muted">
          See your commute to work, school, or anywhere you go — right from this home.
        </p>
        <Link
          href={`/login?redirect=${encodeURIComponent(pathname)}`}
          className="font-medium text-emerald-300 hover:text-emerald-700"
        >
          Sign in to add your places →
        </Link>
      </div>
    );
  }

  if (shouldFetch && commutes === null) {
    return <Skeleton className="h-24 w-full" />;
  }

  if (destinations.length === 0) {
    return (
      <div className="glass rounded-card p-5 text-center">
        <p className="mb-2 text-ink-muted">Add your destinations to see commute times</p>
        <Link href="/settings/destinations" className="font-medium text-emerald-300 hover:text-emerald-700">
          Add destinations →
        </Link>
      </div>
    );
  }

  if (!commutes || commutes.length === 0) {
    return null;
  }

  return (
    <div>
      <h2 className="font-display mb-4 flex items-center gap-2 text-2xl text-ink">
        <Navigation className="h-5 w-5 text-emerald-300" />
        Your commutes
      </h2>
      <ul className="space-y-2">
        {commutes.map((commute) => {
          const minutes = commute.duration_minutes ?? commute.driving_minutes;
          return (
            <li
              key={commute.destination_id}
              className="glass flex items-center justify-between rounded-xl p-4"
            >
              <span className="font-medium text-ink">{commute.label}</span>
              <span className="text-right">
                {typeof minutes === 'number' && (
                  <span className="block font-semibold tabular-nums text-ink">{minutes} min</span>
                )}
                <span className="block text-sm text-ink-muted">
                  {[
                    commute.distance_km ? `${commute.distance_km.toFixed(1)} km` : null,
                    commute.mode,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
